// «Доска»: канбан задач по статусам, перетаскивание между колонками, фильтры, новая задача.
// Постоянные процедуры (разбор рабочих чатов, регулярные проверки агентов) — отдельным списком, чтобы не засорять колонки.
import { useMemo, useState } from 'react'
import { api } from '../api.ts'
import { AgentLabel } from '../components/AgentAvatar.tsx'
import { AgentSelect } from '../components/AgentSelect.tsx'
import { Icon } from '../components/Icon.tsx'
import { NewTaskModal } from '../components/NewTaskModal.tsx'
import { TaskCardView, waitingText } from '../components/TaskCardView.tsx'
import { Badge, Button, ErrorBox, LiveDot, Loading, PageHeader, cx } from '../components/ui.tsx'
import { useAsync, useDebounced } from '../lib/hooks.ts'
import { PRIORITY_ORDER, STATUS_TONES, timeAgo } from '../lib/format.ts'
import { TaskLink } from '../router.tsx'
import { getLive, setAgents, setDirections, useAgentMap, useLive, useNow, useReconnect, useStream, whenVisible } from '../store.ts'
import { loadPref, savePref } from '../theme.ts'
import { toast, toastError } from '../toast.ts'
import {
  TASK_STATUSES,
  TASK_STATUS_LABELS,
  type BoardResponse,
  type Task,
  type TaskCard,
  type TaskStatus,
} from '../types.ts'

const COLUMNS = TASK_STATUSES.filter((s) => s !== 'cancelled') as Exclude<TaskStatus, 'cancelled'>[]

const COLUMN_HINT: Record<Exclude<TaskStatus, 'cancelled'>, string> = {
  inbox: 'Поставлены, ещё не разобраны',
  triage: 'Штаб анализирует и раскладывает',
  in_progress: 'Агенты работают',
  waiting_agents: 'Ждут ответов агентов, подзадач, времени или события — вернутся в работу сами',
  waiting_human: 'Нужно твоё решение или уточнение',
  review: 'Тестировщик и валидатор проверяют',
  done: 'Закрыты за 14 дней',
}

function toCard(t: Task, prev?: TaskCard): TaskCard {
  return {
    subtasks_total: 0,
    subtasks_done: 0,
    running: false,
    queued: false,
    open_escalations: 0,
    messages_count: 0,
    last_activity_at: t.updated_at,
    participants: [],
    ...prev,
    ...t,
  }
}

function compareCards(a: TaskCard, b: TaskCard): number {
  // ждущие тебя и требующие внимания — выше, затем приоритет, затем свежесть
  const att = Number(b.needs_attention || b.open_escalations > 0) - Number(a.needs_attention || a.open_escalations > 0)
  if (att) return att
  const p = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]
  if (p) return p
  return (b.last_activity_at || b.updated_at).localeCompare(a.last_activity_at || a.updated_at)
}

interface Row {
  card: TaskCard
  depth: number
  parentLabel: string | null
}

/** Постоянная процедура: регулярная задача (разбор рабочего чата, анализ направления, ежедневная проверка агента). */
const isRoutine = (t: TaskCard): boolean => t.kind === 'routine'
const isChatWork = (t: TaskCard): boolean => t.created_by.startsWith('system:chat:')

/** Постоянные процедуры списком: что с ней сейчас, чего ждёт, кто ведёт, когда последний раз. */
function RoutineList({ rows }: { rows: TaskCard[] }) {
  const agents = useAgentMap()
  const now = useNow()
  const tz = useLive((s) => s.settings?.agent_timezone)
  const groups = [
    { title: 'Разбор и анализ рабочих чатов', hint: 'робот читает чаты направлений и раз в день пишет анализ', items: rows.filter(isChatWork) },
    { title: 'Процедуры агентов', hint: 'регулярные проверки и сверки, которые агенты ведут сами', items: rows.filter((t) => !isChatWork(t)) },
  ].filter((g) => g.items.length)
  if (!groups.length) {
    return <div className="rounded-card border border-dashed border-line-strong px-3 py-8 text-center text-[13px] text-subtle">Постоянных процедур нет</div>
  }
  return (
    <div className="flex flex-col gap-5">
      {groups.map((g) => (
        <section key={g.title}>
          <h2 className="ct mb-1">
            {g.title} · {g.items.length}
          </h2>
          <p className="mb-2 text-[12.5px] text-subtle">{g.hint}</p>
          <ul className="flex flex-col divide-y divide-line rounded-card border border-line bg-surface">
            {g.items.map((t) => {
              const waiting = t.status === 'waiting_agents' || t.status === 'waiting_human' ? waitingText(t.waiting_on, (id) => agents.get(id)?.name ?? id, tz) : null
              return (
                <li key={t.id} className="flex flex-col gap-1 px-3.5 py-2.5 min-[900px]:flex-row min-[900px]:items-center min-[900px]:gap-3">
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <span className="tnum shrink-0 font-mono text-[11.5px] text-subtle">{t.key}</span>
                    <TaskLink id={t.id} className="min-w-0 truncate text-[13.5px] font-medium text-fg hover:underline" title={t.title}>
                      {t.title}
                    </TaskLink>
                  </div>
                  <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-muted min-[900px]:max-w-[55%] min-[900px]:flex-nowrap">
                    {t.running ? <LiveDot label="работает" /> : <Badge tone={STATUS_TONES[t.status]}>{TASK_STATUS_LABELS[t.status]}</Badge>}
                    {waiting ? (
                      <span className="min-w-0 truncate" title={waiting}>
                        {waiting}
                      </span>
                    ) : null}
                    {t.assignee_agent_id ? <AgentLabel id={t.assignee_agent_id} size="xs" /> : null}
                    <span className="shrink-0 text-subtle">{timeAgo(t.last_activity_at || t.updated_at, now)}</span>
                  </div>
                </li>
              )
            })}
          </ul>
        </section>
      ))}
    </div>
  )
}

export function BoardPage() {
  const directions = useLive((s) => s.directions)
  const [q, setQ] = useState('')
  const [dir, setDir] = useState<string>(() => loadPref('board.dir', ''))
  const [assignee, setAssignee] = useState('')
  const [showSub, setShowSub] = useState<boolean>(() => loadPref('board.subtasks', false))
  const [view, setView] = useState<'tasks' | 'routines'>(() => loadPref<string>('board.view', 'tasks') === 'routines' ? 'routines' : 'tasks')
  const [creating, setCreating] = useState(false)
  const [dragId, setDragId] = useState<number | null>(null)
  const [overCol, setOverCol] = useState<TaskStatus | null>(null)

  const board = useAsync(async (signal) => {
    const r = await api.get<BoardResponse>('/api/board', { signal })
    if (r.directions?.length) setDirections(r.directions)
    if (r.agents?.length && !getLive().agentsLoaded) setAgents(r.agents)
    return r.tasks
  }, [])
  const tasks = board.data
  const setTasks = board.setData
  const refetchSoon = useDebounced(() => whenVisible(board.reload), 1200)

  useStream((ev) => {
    if (ev.type === 'task') {
      const t = ev.data
      setTasks((cur) => {
        if (!cur) return cur
        const i = cur.findIndex((c) => c.id === t.id)
        if (i === -1) return [...cur, toCard(t)]
        const next = [...cur]
        next[i] = toCard(t, next[i])
        return next
      })
      refetchSoon()
    } else if (
      ev.type === 'job' ||
      ev.type === 'escalation' ||
      (ev.type === 'message' && ev.data.task_id !== null)
    ) {
      refetchSoon()
    }
  })
  useReconnect(board.reload)

  const byId = useMemo(() => new Map((tasks ?? []).map((t) => [t.id, t])), [tasks])

  const filtered = useMemo(() => {
    if (!tasks) return []
    const needle = q.trim().toLowerCase()
    const match = (t: TaskCard) =>
      !needle ||
      t.key.toLowerCase().includes(needle) ||
      t.title.toLowerCase().includes(needle) ||
      t.description.toLowerCase().includes(needle)
    return tasks.filter((t) => {
      if (t.status === 'cancelled') return false
      if (dir && !t.direction_ids.includes(dir)) return false
      if (assignee && t.assignee_agent_id !== assignee && !t.participants.includes(assignee)) return false
      if (match(t)) return true
      // корневая карточка видна, если совпала её подзадача
      return !showSub && tasks.some((s) => s.parent_id === t.id && match(s))
    })
  }, [tasks, q, dir, assignee, showSub])

  const childrenOf = useMemo(() => {
    const m = new Map<number, TaskCard[]>()
    for (const t of tasks ?? []) {
      if (t.parent_id === null || t.status === 'cancelled') continue
      const list = m.get(t.parent_id) ?? []
      list.push(t)
      m.set(t.parent_id, list)
    }
    for (const list of m.values()) list.sort((a, b) => a.id - b.id)
    return m
  }, [tasks])

  const columns = useMemo(() => {
    const res = new Map<TaskStatus, Row[]>()
    for (const status of COLUMNS) {
      let cards = filtered.filter((t) => t.status === status && !isRoutine(t))
      if (!showSub) cards = cards.filter((t) => t.parent_id === null || !byId.has(t.parent_id))
      const inCol = new Set(cards.map((c) => c.id))
      const sorted = [...cards].sort(compareCards)
      const kids = new Map<number, TaskCard[]>()
      const tops: TaskCard[] = []
      for (const c of sorted) {
        if (showSub && c.parent_id !== null && inCol.has(c.parent_id)) {
          const l = kids.get(c.parent_id) ?? []
          l.push(c)
          kids.set(c.parent_id, l)
        } else tops.push(c)
      }
      const rows: Row[] = []
      const walk = (c: TaskCard, depth: number) => {
        const parent = c.parent_id !== null ? byId.get(c.parent_id) : undefined
        rows.push({
          card: c,
          depth,
          parentLabel: parent && depth === 0 ? `${parent.key} · ${parent.title}` : null,
        })
        for (const k of kids.get(c.id) ?? []) walk(k, depth + 1)
      }
      for (const t of tops) walk(t, 0)
      res.set(status, rows)
    }
    return res
  }, [filtered, showSub, byId])

  const move = async (id: number, status: TaskStatus) => {
    const card = byId.get(id)
    if (!card || card.status === status) return
    const prev = card.status
    setTasks((cur) => cur?.map((c) => (c.id === id ? { ...c, status } : c)))
    try {
      const t = await api.patch<Task>(`/api/tasks/${id}`, { status })
      setTasks((cur) => cur?.map((c) => (c.id === id ? toCard(t, c) : c)))
      toast(`${card.key} → ${TASK_STATUS_LABELS[status]}`, 'success')
    } catch (e) {
      setTasks((cur) => cur?.map((c) => (c.id === id ? { ...c, status: prev } : c)))
      toastError(e, `Не удалось перенести ${card.key}`)
    }
  }

  // постоянные процедуры — отдельным списком: открытые (закрытые разборы прошлых дней не нужны)
  const routines = useMemo(() => filtered.filter((t) => isRoutine(t) && t.status !== 'done').sort(compareCards), [filtered])
  const total = filtered.filter((t) => !isRoutine(t)).length
  const waitingCount = columns.get('waiting_human')?.length ?? 0
  const pickView = (v: 'tasks' | 'routines') => {
    setView(v)
    savePref('board.view', v)
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="px-4 pt-5 pb-3 min-[720px]:px-7 min-[720px]:pt-6">
        <PageHeader
          className="mb-4"
          title="Доска"
          lede={
            tasks ? (
              <span className="tnum">
                {total} {total % 10 === 1 && total % 100 !== 11 ? 'задача' : [2, 3, 4].includes(total % 10) && ![12, 13, 14].includes(total % 100) ? 'задачи' : 'задач'} на доске
                {routines.length ? ` · постоянных процедур: ${routines.length}` : ''}
                {waitingCount ? (
                  <>
                    {' · '}
                    <span className="font-medium text-warn">ждут тебя: {waitingCount}</span>
                  </>
                ) : null}
              </span>
            ) : (
              'Задачи агентов по статусам'
            )
          }
          actions={
            <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
              Новая задача
            </Button>
          }
        />
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex h-9 overflow-hidden rounded-ctl border border-line" role="tablist" aria-label="Что показывать">
            {([['tasks', 'Задачи'], ['routines', `Постоянные · ${routines.length}`]] as const).map(([v, label]) => (
              <button
                key={v}
                type="button"
                role="tab"
                aria-selected={view === v}
                className={cx('px-3 text-[13px]', view === v ? 'bg-accent text-accent-fg' : 'bg-surface text-muted hover:bg-surface-2 hover:text-fg')}
                onClick={() => pickView(v)}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="relative w-full min-w-[180px] sm:w-72">
            <Icon name="search" size={15} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-subtle" />
            <input
              type="search"
              className="ctl h-9 pl-8"
              placeholder="Фильтр: OMA-12, заголовок, текст…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Фильтр задач на доске"
            />
          </div>
          <select
            className="ctl h-9 w-auto min-w-[140px] flex-1 sm:flex-none"
            value={dir}
            onChange={(e) => {
              setDir(e.target.value)
              savePref('board.dir', e.target.value)
            }}
            aria-label="Фильтр по направлению"
          >
            <option value="">Все направления</option>
            {directions.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
          <AgentSelect
            value={assignee}
            onChange={setAssignee}
            emptyLabel="Все агенты"
            ariaLabel="Фильтр по агенту"
            className="h-9 w-auto min-w-[140px] flex-1 sm:max-w-[240px] sm:flex-none"
          />
          <label className={cx('inline-flex h-9 cursor-pointer items-center gap-2 rounded-ctl px-1.5 text-[13px] text-muted select-none hover:text-fg', view === 'routines' && 'hidden')}>
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={showSub}
              onChange={(e) => {
                setShowSub(e.target.checked)
                savePref('board.subtasks', e.target.checked)
              }}
            />
            Показывать подзадачи
          </label>
        </div>
      </div>

      {board.error && !tasks ? (
        <div className="px-4 min-[720px]:px-7">
          <ErrorBox error={board.error} onRetry={board.reload} />
        </div>
      ) : !tasks ? (
        <div className="px-4 min-[720px]:px-7">
          <Loading label="Загружаю доску…" />
        </div>
      ) : view === 'routines' ? (
        <div className="scroll-thin min-h-0 flex-1 overflow-y-auto px-4 pt-1 pb-6 min-[720px]:px-7">
          <RoutineList rows={routines} />
        </div>
      ) : (
        // на телефоне колонка почти во весь экран, полоса листается по колонке (snap); вбок прокручивается только она, не страница.
        // Пока карточку тащат, привязки нет: иначе она гасит автопрокрутку и дальше соседней колонки не перенести
        <div
          className={cx(
            'scroll-thin flex min-h-0 flex-1 gap-3 overflow-x-auto overflow-y-hidden px-4 pt-1 pb-4 max-sm:scroll-px-4 max-sm:overscroll-x-contain min-[720px]:px-7 min-[720px]:pb-6',
            dragId === null && 'max-sm:snap-x max-sm:snap-mandatory',
          )}
          role="list"
          aria-label="Колонки доски"
        >
          {COLUMNS.map((status) => {
            const rows = columns.get(status) ?? []
            const emphasized = status === 'waiting_human'
            const over = overCol === status && dragId !== null
            return (
              <section
                key={status}
                role="listitem"
                aria-label={`${TASK_STATUS_LABELS[status]}: ${rows.length}`}
                className={cx(
                  'flex max-h-full w-[290px] shrink-0 flex-col rounded-[12px] border transition-colors max-sm:w-[85vw] max-sm:snap-start',
                  emphasized ? 'border-warn/25 bg-warn-soft/45' : 'border-line bg-surface-2',
                  over && 'border-dashed border-accent/60 bg-accent-soft',
                )}
                onDragOver={(e) => {
                  if (dragId === null) return
                  e.preventDefault()
                  e.dataTransfer.dropEffect = 'move'
                  if (overCol !== status) setOverCol(status)
                }}
                onDragLeave={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOverCol((c) => (c === status ? null : c))
                }}
                onDrop={(e) => {
                  e.preventDefault()
                  const id = Number(e.dataTransfer.getData('text/plain')) || dragId
                  setOverCol(null)
                  setDragId(null)
                  if (id) void move(id, status)
                }}
              >
                <header className="flex items-center gap-2 px-3 pt-3 pb-2" title={COLUMN_HINT[status]}>
                  <h2 className={cx('ct', emphasized && rows.length > 0 && 'text-warn')}>{TASK_STATUS_LABELS[status]}</h2>
                  <span
                    className={cx(
                      'tnum rounded-full px-1.5 text-[11px] leading-[18px] font-semibold',
                      emphasized && rows.length ? 'bg-warn-soft text-warn' : 'bg-surface-3 text-muted',
                    )}
                  >
                    {rows.length}
                  </span>
                </header>
                <div className="scroll-thin flex min-h-[80px] flex-1 flex-col gap-2 overflow-y-auto px-2 pb-2">
                  {rows.length === 0 ? (
                    <div className="rounded-card border border-dashed border-line-strong px-3 py-5 text-center text-[12.5px] text-subtle">
                      {emphasized ? 'Никто не ждёт — всё под контролем' : 'Пусто'}
                    </div>
                  ) : (
                    rows.map((r) => (
                      <TaskCardView
                        key={r.card.id}
                        card={r.card}
                        depth={r.depth}
                        parentLabel={r.parentLabel}
                        nested={!showSub ? childrenOf.get(r.card.id) : undefined}
                        dragging={dragId === r.card.id}
                        onDragStart={setDragId}
                        onDragEnd={() => {
                          setDragId(null)
                          setOverCol(null)
                        }}
                      />
                    ))
                  )}
                </div>
              </section>
            )
          })}
        </div>
      )}

      {creating ? (
        <NewTaskModal
          onClose={() => setCreating(false)}
          initialDirections={dir ? [dir] : []}
          onCreated={(t) =>
            setTasks((cur) => (cur && !cur.some((c) => c.id === t.id) ? [...cur, toCard(t)] : cur))
          }
        />
      ) : null}
    </div>
  )
}
